/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmSavedViews.ts
 * PURPOSE: CRM named filter views — list, save from current URL params, optimistic delete (dashboard-service `/api/v1/crm/saved-views`)
 * DEPENDENCIES: react, react-router-dom, @shared/lib/frontend/api/config, ../data/mockCrmData.dev, ../utils/crmFilterUrl
 * PUBLIC_API: useCrmSavedViews, UseCrmSavedViewsOptions, CrmSavedViewsEntity, CrmSavedView
 * SIDE_EFFECTS: fetch GET/POST/DELETE; optional dev merge of **`DEV_SEED_CRM_SAVED_VIEWS`**
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 137/200
 * RECENT: 2026-05-13 [2.2.960] Dashboard **`GET /api/v1/crm/saved-views?entity=tasks`** requires current **`dashboard-service`** image (**`CrmEntity.tasks`**) and shared **`get_db`** must not wrap **`RequestValidationError`** as **500** — root **`CHANGELOG` [2.2.960]`**. 2026-05-08 [2.2.884] **`tasks`** profile saved-view keys merge with **`DEV_SEED_CRM_SAVED_VIEWS`** — root **`CHANGELOG` [2.2.884]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.758] — initial hook — CHANGELOG [2.2.758]
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';
import { DEV_SEED_CRM_SAVED_VIEWS } from '../data/mockCrmData.dev';
import { filterRecordFromSearchParams, type CrmUrlFilterProfile } from '../utils/crmFilterUrl';

export type CrmSavedViewsEntity = Exclude<CrmUrlFilterProfile, 'home'>;

export type CrmSavedView = {
  id: string;
  name: string;
  entity: CrmSavedViewsEntity;
  filters: Record<string, string>;
  savedAt: string;
};

type ListJson = {
  views?: Array<{ id: string; name: string; entity: string; filters: Record<string, string>; savedAt: string }>;
};

function isEntity(v: string): v is CrmSavedViewsEntity {
  return v === 'deals' || v === 'contacts' || v === 'territory' || v === 'tasks' || v === 'pipeline';
}

function mergeDevSeeds(entity: CrmSavedViewsEntity, server: CrmSavedView[]): CrmSavedView[] {
  if (!import.meta.env.DEV) return server;
  const seeds = DEV_SEED_CRM_SAVED_VIEWS[entity] ?? [];
  const seedRows: CrmSavedView[] = seeds.map((s) => ({
    id: s.id,
    name: s.name,
    entity: s.entity,
    filters: { ...s.filters },
    savedAt: '1970-01-01T00:00:00.000Z',
  }));
  return [...seedRows, ...server];
}

export type UseCrmSavedViewsOptions = {
  /** When false, no fetch and save or delete are no-ops (FilterBar without entity). */
  enabled?: boolean;
};

export function useCrmSavedViews(entity: CrmSavedViewsEntity, options?: UseCrmSavedViewsOptions) {
  const enabled = options?.enabled !== false;
  const [searchParams] = useSearchParams();
  const [views, setViews] = useState<CrmSavedView[]>([]);
  const [isLoading, setIsLoading] = useState(() => enabled);
  const profile = entity as CrmUrlFilterProfile;

  const load = useCallback(async () => {
    if (!enabled) {
      setViews([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const url = `${API_CONFIG.baseURL}/api/v1/crm/saved-views?entity=${encodeURIComponent(entity)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const body = (await res.json()) as ListJson;
      const raw = Array.isArray(body.views) ? body.views : [];
      const server: CrmSavedView[] = raw
        .filter((r) => r && isEntity(String(r.entity ?? '')))
        .map((r) => ({
          id: String(r.id),
          name: String(r.name ?? ''),
          entity: r.entity as CrmSavedViewsEntity,
          filters: typeof r.filters === 'object' && r.filters ? { ...r.filters } : {},
          savedAt: String(r.savedAt ?? ''),
        }));
      setViews(mergeDevSeeds(entity, server));
    } catch {
      setViews(mergeDevSeeds(entity, []));
    } finally {
      setIsLoading(false);
    }
  }, [enabled, entity]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveView = useCallback(
    async (name: string) => {
      if (!enabled) return;
      const filters = filterRecordFromSearchParams(profile, searchParams);
      const res = await fetch(`${API_CONFIG.baseURL}/api/v1/crm/saved-views`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, entity, filters }),
      });
      if (!res.ok) throw new Error(await res.text());
      await load();
    },
    [enabled, entity, load, profile, searchParams]
  );

  const deleteView = useCallback(
    async (id: string) => {
      if (!enabled) return;
      if (id.startsWith('dev-seed-')) {
        setViews((prev) => prev.filter((v) => v.id !== id));
        return;
      }
      let snapshot: CrmSavedView[] = [];
      setViews((prev) => {
        snapshot = prev;
        return prev.filter((v) => v.id !== id);
      });
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/api/v1/crm/saved-views/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok && res.status !== 404) throw new Error(await res.text());
      } catch {
        setViews(snapshot);
      }
    },
    [enabled]
  );

  return { views, saveView, deleteView, isLoading };
}
